/**
 * AccountingSettingsTab — Configuración de cuentas contables por defecto
 * Permite mapear las cuentas de asientos automáticos (AccountingDefaults):
 * generales (ventas, compras, IVA, banco, factoring, dif. de cambio) y de nómina.
 */
import { useState, useEffect, useMemo } from 'react'
import { Loader2, Save, Plus, Trash2 } from 'lucide-react'
import { useToast } from '@/components/Toast'
import { useTenantTax } from '@/modules/config/hooks/useConfig'
import { AccountAutocomplete, type AccountOption } from './AccountAutocomplete'
import {
  useAccountingDefaults,
  useUpdateAccountingDefaults,
  useAccounts,
  usePayrollDeductionTypes,
  useSavePayrollDeductionType,
  useDeletePayrollDeductionType,
  type AccountingDefaults,
  PREVIRED_CCAF_FIELDS,
  type PayrollDeductionType,
  type PreviredCcafField,
} from '../hooks/useAccounting'

/**
 * Las cuentas del mapeo contable.
 *
 * ⚠️ Es una función y no una constante porque tres de sus rótulos nombran el
 * impuesto, y ese nombre lo declara la localización del tenant: un contador
 * peruano configurando su plan veía «IVA Débito Fiscal» cuando su impuesto es el
 * IGV. El resto de los rótulos es agnóstico y no cambia.
 */
export const accountFields = (impuesto: string): {
  key: keyof AccountingDefaults
  label: string
  description: string
}[] => [
  {
    key: 'account_receivable',
    label: 'Cuentas por Cobrar',
    description: 'Dr al emitir factura de venta / Cr al confirmar pago de cliente',
  },
  {
    key: 'account_sales_revenue',
    label: 'Ingresos por Ventas',
    description: 'Cr al emitir factura de venta (subtotal neto)',
  },
  {
    key: 'account_vat_payable',
    label: `${impuesto} Débito Fiscal`,
    description: 'Cr al emitir factura de venta (monto de impuesto)',
  },
  {
    key: 'account_cash_bank',
    label: 'Caja / Banco',
    description: 'Dr al confirmar pago de cliente / Cr al confirmar pago a proveedor',
  },
  {
    key: 'account_factoring_commission',
    label: 'Comisión de Factoring',
    description: 'Dr al registrar el pago de una operación de factoring (comisión cobrada)',
  },
  {
    key: 'account_payable',
    label: 'Cuentas por Pagar',
    description: 'Cr al aprobar factura de proveedor / Dr al confirmar pago a proveedor',
  },
  {
    key: 'account_purchase_exp',
    label: 'Gastos de Compras',
    description: 'Dr al aprobar factura de proveedor (subtotal neto)',
  },
  {
    key: 'account_vat_credit',
    label: `${impuesto} Crédito Fiscal`,
    description: 'Dr al aprobar factura de proveedor (monto de impuesto)',
  },
  {
    key: 'account_vat_withheld',
    label: `${impuesto} Retenido por Pagar`,
    description: 'Cr al emitir Factura de Compra (retención IVA tipo 46)',
  },
  {
    key: 'account_non_billable_charges',
    label: 'Cargos No Facturables (Compras)',
    description: 'Dr del monto no facturable en compras (telecom: equipos en cuotas / cargos de terceros). La CxP queda por el Valor a Pagar. Opcional: si se deja sin asignar, va a Gastos de Compras',
  },
  {
    key: 'account_exchange_difference',
    label: 'Diferencia de Cambio',
    description: 'Ganancia/pérdida al pagar facturas en moneda extranjera a una tasa distinta de la de emisión',
  },
]

/**
 * Las claves, derivadas una sola vez.
 *
 * 🔴 El efecto de carga inicial usa ESTO y no `accountFields(tax.name)`: las
 * claves no dependen del impuesto, solo los rótulos. Si el efecto dependiera del
 * array del impuesto, la llegada de la localización lo reejecutaría y
 * **reiniciaría el formulario**, borrando lo que el usuario tuviera escrito. Es el
 * patrón del modal que se reseteaba por refetch.
 */
export const ACCOUNT_KEYS = accountFields('').map(f => f.key)

const PAYROLL_ACCOUNT_FIELDS: {
  key: keyof AccountingDefaults
  label: string
  description: string
}[] = [
  { key: 'account_salaries_expense', label: 'Remuneraciones (Gasto)', description: 'Dr sueldos brutos al contabilizar la provisión del período' },
  { key: 'account_employer_contributions_expense', label: 'Aporte Empleador (Gasto)', description: 'Dr aportes patronales (SIS, mutual, cesantía empleador, reforma, expectativa de vida). Opcional: si se deja sin asignar, van junto a Remuneraciones (Gasto)' },
  { key: 'account_salaries_payable', label: 'Remuneraciones por Pagar', description: 'Cr en la provisión / Dr al pagar la nómina' },
  { key: 'account_afp_payable', label: 'AFP por Pagar', description: 'Cr cotización AFP del trabajador' },
  { key: 'account_health_payable', label: 'Salud por Pagar', description: 'Cr cotización de salud obligatoria (7% legal, FONASA o Isapre)' },
  { key: 'account_health_additional_payable', label: 'Salud Adicional por Pagar', description: 'Cr cotización de salud adicional al 7% (planes Isapre en UF). Opcional: si se deja sin asignar, se agrupa en Salud por Pagar' },
  { key: 'account_tax_2cat_payable', label: 'Impuesto Único por Pagar', description: 'Cr Impuesto Único de 2ª Categoría retenido' },
  { key: 'account_unemployment_payable', label: 'Cesantía por Pagar', description: 'Cr seguro de cesantía (empleado + empleador)' },
  { key: 'account_mutual_payable', label: 'Mutual por Pagar', description: 'Cr mutual de seguridad y SANNA (empleador). Si no se configura la cuenta de SIS, también agrupa el SIS' },
  { key: 'account_sis_payable', label: 'SIS por Pagar', description: 'Cr Seguro de Invalidez y Sobrevivencia (empleador). Opcional: si se deja sin asignar, se agrupa en Mutual por Pagar' },
  { key: 'account_apv_payable', label: 'APV por Pagar', description: 'Cr APV (solo si hay empleados con APV configurado)' },
  { key: 'account_business_salary_expense', label: 'Sueldo Empresarial (Gasto)', description: 'Dr sueldo empresarial del socio/dueño (Art. 31 N°6 LIR). Opcional: si se deja sin asignar, va a Remuneraciones (Gasto)' },
  { key: 'account_pension_reform_payable', label: 'Reforma Previsional por Pagar', description: 'Cr aporte del empleador de la Reforma Previsional (Ley 21.735). Opcional: si se deja sin asignar, se agrupa en Mutual por Pagar' },
  { key: 'account_other_deductions_payable', label: 'Otros Descuentos por Pagar', description: 'Cr otros descuentos sin un tipo con cuenta propia (cuenta de respaldo). Requerida solo si hay liquidaciones con otros descuentos no mapeados' },
  { key: 'account_voluntary_savings_payable', label: 'Ahorro Previsional Voluntario por Pagar', description: 'Cr Cuenta 2 y Depósito Convenido. Opcional: si se deja sin asignar, se agrupa en APV por Pagar' },
  { key: 'account_family_allowance_receivable', label: 'Asignación Familiar por Recuperar', description: 'Dr por la asignación familiar pagada (activo por recuperar del Estado/IPS, o cuenta CCAF para netear). Requerida solo si hay empleados con asignación familiar' },
]

const AUTO_APPROVE_FIELDS: {
  key: keyof AccountingDefaults
  label: string
  description: string
}[] = [
  {
    key: 'auto_approve_sales',
    label: 'Ventas',
    description: 'Facturas de venta emitidas (DTE aceptado / contabilizar).',
  },
  {
    key: 'auto_approve_collections',
    label: 'Cobranza',
    description: 'Cobros de clientes confirmados.',
  },
  {
    key: 'auto_approve_purchases',
    label: 'Compras',
    description: 'Facturas de proveedor aprobadas (manual, XML, RCV, internacionales, DTE recibido).',
  },
  {
    key: 'auto_approve_payments',
    label: 'Pagos',
    description: 'Pagos a proveedor confirmados (incluye diferencia de cambio).',
  },
  {
    key: 'auto_approve_payroll',
    label: 'Nómina',
    description: 'Provisión y pago de remuneraciones.',
  },
  {
    key: 'auto_approve_honorarios',
    label: 'Honorarios',
    description: 'Boletas de honorarios contabilizadas.',
  },
]

const SETTINGS_TABS = [
  { key: 'general', label: 'Cuentas Generales' },
  { key: 'payroll', label: 'Cuentas de Nómina' },
  { key: 'deductions', label: 'Tipos de Descuento' },
  { key: 'auto', label: 'Aprobación Automática' },
] as const
type SettingsTabKey = typeof SETTINGS_TABS[number]['key']

export function AccountingSettingsTab() {
  const { data: defaults, isLoading } = useAccountingDefaults()
  const tax = useTenantTax()
  // `useMemo` porque el `forEach` de la carga inicial lo tiene como dependencia:
  // sin memo, un array nuevo en cada render reejecutaria ese efecto.
  const camposDeCuentas = useMemo(() => accountFields(tax.name), [tax.name])
  const { data: accountsData, isLoading: loadingAccounts } = useAccounts({ is_imputable: 'true', is_active: 'true' })
  const update = useUpdateAccountingDefaults()

  const accounts = accountsData?.accounts ?? []
  const toast = useToast()

  const [form, setForm] = useState<Partial<AccountingDefaults>>({})
  const [tab, setTab] = useState<SettingsTabKey>('general')

  useEffect(() => {
    if (defaults) {
      const initial: Partial<AccountingDefaults> = {}
      ACCOUNT_KEYS.forEach((key) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(initial as any)[key] = (defaults[key] as string | null) ?? null
      })
      PAYROLL_ACCOUNT_FIELDS.forEach(({ key }) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(initial as any)[key] = (defaults[key] as string | null) ?? null
      })
      AUTO_APPROVE_FIELDS.forEach(({ key }) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(initial as any)[key] = Boolean(defaults[key])
      })
      setForm(initial)
    }
  }, [defaults])

  const handleSave = () => {
    update.mutate(form, {
      onSuccess: () => toast.success('Configuración guardada correctamente.'),
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        toast.error(err.response?.data?.error?.message ?? 'Error al guardar')
      },
    })
  }

  if (isLoading || loadingAccounts) {
    return (
      <div className="flex items-center justify-center py-16 text-content-subtle">
        <Loader2 size={20} className="animate-spin mr-2" />Cargando configuración...
      </div>
    )
  }

  return (
    <div className="max-w-5xl space-y-6">
      {/* Barra de pestañas + guardar. El guardado global aplica a las cuentas
          (un solo objeto AccountingDefaults); los Tipos de Descuento se guardan
          por fila, por eso ahí se oculta el botón global. El estado del formulario
          se conserva al cambiar de pestaña. */}
      <div className="flex items-center justify-between border-b border-divider flex-wrap gap-2">
        <nav className="flex gap-1 -mb-px">
          {SETTINGS_TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-content-muted hover:text-content'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
        {tab !== 'deductions' && (
          <button
            onClick={handleSave}
            disabled={update.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 mb-1 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover disabled:opacity-50"
          >
            {update.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar
          </button>
        )}
      </div>

      {tab === 'general' && (
        <div className="space-y-4">
          <p className="text-sm text-content-muted">
            Cuentas usadas para generar comprobantes contables automáticamente al registrar
            documentos de venta, pagos y facturas de proveedor.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-divider rounded-xl border border-divider overflow-hidden">
            {camposDeCuentas.map(({ key, label, description }) => (
              <div key={key} className="px-5 py-4 bg-surface">
                <label className="block text-sm font-medium text-content mb-0.5">{label}</label>
                <p className="text-xs text-content-subtle mb-2">{description}</p>
                <AccountAutocomplete aria-label={label}
                  accounts={accounts}
                  value={(form[key] as string | null) ?? null}
                  onChange={id => setForm(prev => ({ ...prev, [key]: id }))}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'payroll' && (
        <div className="space-y-4">
          <p className="text-sm text-content-muted">
            Cuentas usadas al contabilizar la provisión y el pago de remuneraciones. Varias son
            opcionales: si se dejan sin asignar usan una cuenta de respaldo (ver cada descripción).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-divider rounded-xl border border-divider overflow-hidden">
            {PAYROLL_ACCOUNT_FIELDS.map(({ key, label, description }) => (
              <div key={key} className="px-5 py-4 bg-surface">
                <label className="block text-sm font-medium text-content mb-0.5">{label}</label>
                <p className="text-xs text-content-subtle mb-2">{description}</p>
                <AccountAutocomplete aria-label={label}
                  accounts={accounts}
                  value={(form[key] as string | null) ?? null}
                  onChange={id => setForm(prev => ({ ...prev, [key]: id }))}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'deductions' && <PayrollDeductionTypesSection accounts={accounts} />}

      {tab === 'auto' && (
        <div className="space-y-4">
          <p className="text-sm text-content-muted">
            Por entidad, los comprobantes que Clobi genera automáticamente pueden nacer
            <span className="font-medium text-content"> aprobados</span> (asiento firme) en vez de en
            borrador. Si un comprobante no cuadra o le faltan cuentas configuradas, quedará en
            <span className="font-medium text-content"> borrador</span> para revisión manual.
          </p>
          <div className="bg-glass backdrop-blur-glass rounded-card border border-divider divide-y divide-divider shadow-card">
            {AUTO_APPROVE_FIELDS.map(({ key, label, description }) => {
              const checked = Boolean(form[key])
              return (
                <div key={key} className="flex items-start justify-between gap-4 px-5 py-4">
                  <div className="min-w-0">
                    <label htmlFor={key} className="block text-sm font-medium text-content">{label}</label>
                    <p className="text-xs text-content-subtle mt-0.5">{description}</p>
                  </div>
                  <button
                    id={key}
                    type="button"
                    role="switch"
                    aria-checked={checked}
                    onClick={() => setForm(prev => ({ ...prev, [key]: !checked }))}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 focus:ring-offset-surface ${
                      checked ? 'bg-primary' : 'bg-content-subtle/30'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        checked ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Tipos de descuento (catálogo → cuenta contable) ─────────────────────────

function PayrollDeductionTypesSection({ accounts }: { accounts: AccountOption[] }) {
  const { data: types, isLoading } = usePayrollDeductionTypes()
  const save = useSavePayrollDeductionType()
  const toast = useToast()
  const empty = {
    code: '', name: '', account: '', nature: 'liability' as const,
    previred_field: '' as PreviredCcafField | '',
  }
  const [draft, setDraft] = useState<{
    code: string
    name: string
    account: string
    nature: 'liability' | 'asset'
    previred_field: PreviredCcafField | ''
  }>(empty)

  const handleAdd = () => {
    if (!draft.code.trim() || !draft.name.trim()) {
      toast.error('Código y nombre son obligatorios.')
      return
    }
    save.mutate(
      {
        code: draft.code.trim(), name: draft.name.trim(),
        account: draft.account || null, nature: draft.nature,
        previred_field: draft.previred_field,
      },
      {
        onSuccess: () => { setDraft(empty); toast.success('Tipo de descuento agregado.') },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          toast.error(err.response?.data?.error?.message ?? 'Error al agregar')
        },
      },
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-content">Tipos de descuento</h3>
        <p className="text-sm text-content-muted mt-1">
          Cada tipo de "otro descuento" (crédito CCAF, seguro complementario, anticipo, préstamo, etc.)
          se imputa a la cuenta que le asignes al centralizar la nómina. Lo que no tenga un tipo con cuenta
          cae en <span className="font-medium text-content">Otros Descuentos por Pagar</span>.
        </p>
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          {/* ⚠️ NO se migra al `DataTable`, y es la última tabla del producto sin
              razón escrita. El bloqueo es la FILA DE ALTA del final: el
              componente recibe datos y esa fila no es un dato sino un
              formulario dentro del `<tbody>`. Sacarla afuera cambia la
              pantalla —hoy se agrega un tipo escribiendo en la misma grilla
              donde se ven los que ya están— y meterla como dato haría que el
              orden por clic la mezclara entre las filas reales. */}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-divider text-content-muted text-xs uppercase">
                <th className="text-left px-4 py-2 font-medium">Código</th>
                <th className="text-left px-4 py-2 font-medium">Nombre</th>
                <th className="text-left px-4 py-2 font-medium">Cuenta</th>
                <th className="text-left px-4 py-2 font-medium">Naturaleza</th>
                <th className="text-left px-4 py-2 font-medium">Se declara a la CCAF en</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {isLoading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-content-subtle">Cargando…</td></tr>
              ) : (types ?? []).length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-content-subtle">Sin tipos. Agrega el primero abajo.</td></tr>
              ) : (
                (types ?? []).map(t => <DeductionTypeRow key={t.id} type={t} accounts={accounts} />)
              )}
              <tr className="bg-canvas/40">
                <td className="px-4 py-2">
                  <input value={draft.code} onChange={e => setDraft(d => ({ ...d, code: e.target.value }))}
                    aria-label="Código del tipo a agregar"
                    placeholder="credito_ccaf"
                    className="w-full border border-divider rounded px-2 py-1.5 text-sm bg-surface focus:ring-2 focus:ring-primary" />
                </td>
                <td className="px-4 py-2">
                  <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                    aria-label="Nombre del tipo a agregar"
                    placeholder="Crédito CCAF"
                    className="w-full border border-divider rounded px-2 py-1.5 text-sm bg-surface focus:ring-2 focus:ring-primary" />
                </td>
                <td className="px-4 py-2 min-w-[220px]">
                  <AccountAutocomplete
                    accounts={accounts}
                    value={draft.account || null}
                    onChange={id => setDraft(d => ({ ...d, account: id ?? '' }))}
                    aria-label="Cuenta del tipo a agregar"
                    placeholder="— Otros Descuentos por Pagar —"
                  />
                </td>
                <td className="px-4 py-2">
                  <select value={draft.nature} onChange={e => setDraft(d => ({ ...d, nature: e.target.value as 'liability' | 'asset' }))}
                    aria-label="Naturaleza del tipo a agregar"
                    className="w-full border border-divider rounded px-2 py-1.5 text-sm bg-surface">
                    <option value="liability">Pasivo</option>
                    <option value="asset">Activo (préstamo/anticipo)</option>
                  </select>
                </td>
                <td className="px-4 py-2">
                  <select
                    value={draft.previred_field}
                    onChange={e => setDraft(d => ({ ...d, previred_field: e.target.value as PreviredCcafField | '' }))}
                    aria-label="Campo del archivo Previred al que se declara"
                    className="w-full border border-divider rounded px-2 py-1.5 text-sm bg-surface"
                    title="Si la empresa retiene este descuento para la CCAF, acá se indica a qué campo del archivo Previred viaja. Sin esto la caja no lo acredita."
                  >
                    <option value="">— No se declara a la CCAF —</option>
                    {PREVIRED_CCAF_FIELDS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={handleAdd} disabled={save.isPending}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-on-primary text-xs rounded-lg hover:bg-primary-hover disabled:opacity-50">
                    <Plus size={13} /> Agregar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function DeductionTypeRow({ type, accounts }: { type: PayrollDeductionType; accounts: AccountOption[] }) {
  const save = useSavePayrollDeductionType()
  const del = useDeletePayrollDeductionType()
  const toast = useToast()
  const [account, setAccount] = useState<string>(type.account ?? '')
  const [nature, setNature] = useState<'liability' | 'asset'>(type.nature)
  const [previredField, setPreviredField] = useState<PreviredCcafField | ''>(
    type.previred_field ?? '')
  const dirty =
    (account || '') !== (type.account ?? '') ||
    nature !== type.nature ||
    previredField !== (type.previred_field ?? '')

  const handleSave = () => {
    save.mutate({ id: type.id, account: account || null, nature, previred_field: previredField }, {
      onSuccess: () => toast.success('Tipo actualizado.'),
      onError: () => toast.error('Error al guardar'),
    })
  }
  const handleDelete = () => {
    del.mutate(type.id, {
      onSuccess: () => toast.success('Tipo eliminado.'),
      onError: () => toast.error('No se pudo eliminar'),
    })
  }

  return (
    <tr>
      <td className="px-4 py-2 font-mono text-xs text-content-subtle">{type.code}</td>
      <td className="px-4 py-2 text-content">{type.name}</td>
      <td className="px-4 py-2 min-w-[220px]">
        <AccountAutocomplete aria-label={`Cuenta — ${type.name}`}
          accounts={accounts}
          value={account || null}
          onChange={id => setAccount(id ?? '')}
          placeholder="— Otros Descuentos por Pagar —"
        />
      </td>
      <td className="px-4 py-2">
        <select aria-label={`Naturaleza — ${type.name}`} value={nature} onChange={e => setNature(e.target.value as 'liability' | 'asset')}
          className="w-full border border-divider rounded px-2 py-1.5 text-sm bg-surface">
          <option value="liability">Pasivo</option>
          <option value="asset">Activo (préstamo/anticipo)</option>
        </select>
      </td>
      <td className="px-4 py-2">
        <select aria-label={`Campo Previred — ${type.name}`}
          value={previredField}
          onChange={e => setPreviredField(e.target.value as PreviredCcafField | '')}
          className="w-full border border-divider rounded px-2 py-1.5 text-sm bg-surface"
          title="Si la empresa retiene este descuento para la CCAF, acá se indica a qué campo del archivo Previred viaja. Sin esto la caja no lo acredita y el trabajador sigue apareciendo como deudor."
        >
          <option value="">— No se declara a la CCAF —</option>
          {PREVIRED_CCAF_FIELDS.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        {dirty && (
          <button onClick={handleSave} disabled={save.isPending}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-primary text-on-primary text-xs rounded-lg hover:bg-primary-hover disabled:opacity-50 mr-1">
            <Save size={12} /> Guardar
          </button>
        )}
        <button onClick={handleDelete} disabled={del.isPending} title="Eliminar"
          className="inline-flex items-center px-2 py-1.5 text-danger-content-soft hover:bg-red-500/10 rounded-lg">
          <Trash2 size={13} />
        </button>
      </td>
    </tr>
  )
}
